// Excel export — the whole roadmap or just what the current filters show.
//
// The sheet builders are pure (and unit-tested); exportRoadmap() does the
// file write. write-excel-file is loaded on demand so it stays out of the
// main bundle — most visits never export.

import type { Initiative, Person, Update } from "./types";
import type { Filters } from "@/components/roadmap/filter-bar";
import {
  ALL_TAGS, QUARTERS, STATUS_LABELS, TEAMS, localISO, normalizeQuarterId, quarterLabel, tagNum, toDate,
} from "./constants";
import { byStart } from "./ordering";
import { NO_WORKSTREAM } from "./group-by";
import { actualShipped, shipDeltaDays } from "./baseline";

// "window" = exactly what the timeline shows: the filtered set, limited to
// the visible four quarters (or the picked quarters).
export type ExportScope = "all" | "filtered" | "window";

type Cell = string | number | Date | null;
type HeaderCell = { value: string; fontWeight: "bold"; backgroundColor: string };

const HEADER_FILL = "#EDEEF0";

// One entry per column: header text, width (Excel character units), value.
const COLUMNS: { header: string; width: number; value: (i: Initiative, ctx: Ctx) => Cell }[] = [
  { header: "Initiative", width: 40, value: i => i.name },
  { header: "Description", width: 60, value: i => i.tldr || null },
  { header: "Status", width: 13, value: i => STATUS_LABELS[i.status] ?? i.status },
  { header: "Team", width: 22, value: i => teamName(i.teamId) },
  { header: "Pod", width: 28, value: i => podName(i.teamId, i.podId) },
  { header: "Workstream", width: 24, value: i => i.workstream?.trim() || null },
  { header: "Owner", width: 22, value: (i, c) => c.people.get(i.driId)?.name ?? null },
  { header: "Owner email", width: 30, value: (i, c) => c.people.get(i.driId)?.email ?? null },
  { header: "Tags", width: 32, value: i => i.pillarIds.map(tagName).join(", ") || null },
  { header: "Quarter", width: 11, value: i => quarterLabel(i.targetQ) },
  { header: "Start", width: 12, value: i => excelDate(toDate(i.start)) },
  { header: "Target", width: 12, value: i => excelDate(toDate(i.target)) },
  { header: "Original target", width: 15, value: i => excelDate(toDate(i.originalTarget)) },
  { header: "Shipped", width: 12, value: i => excelDate(toDate(actualShipped(i))) },
  { header: "Days vs. original target", width: 22, value: i => shipDeltaDays(i) },
  { header: "Latest update", width: 60, value: (i, c) => plainUpdate(c.latest.get(i.id)?.body) },
  { header: "Latest update date", width: 18, value: (i, c) => excelDate(isoDate(c.latest.get(i.id)?.createdAt)) },
  { header: "Latest update by", width: 22, value: (i, c) => c.people.get(c.latest.get(i.id)?.authorId ?? "")?.name ?? null },
  { header: "Jira", width: 30, value: i => i.jira || null },
  { header: "Brief", width: 30, value: i => i.notion || null },
  { header: "Design", width: 30, value: i => i.design || null },
];

type Ctx = { people: Map<string, Person>; latest: Map<string, Update> };

// Newest update per initiative. Doesn't trust the API's ordering.
export function latestUpdates(updates: Update[]): Map<string, Update> {
  const out = new Map<string, Update>();
  for (const u of updates) {
    const cur = out.get(u.initiativeId);
    if (!cur || u.createdAt > cur.createdAt) out.set(u.initiativeId, u);
  }
  return out;
}

// Update bodies are markdown-lite (see update-markdown.tsx); in a cell only
// the bold markers read as noise. Bullets and line breaks stay as typed.
function plainUpdate(body: string | undefined): string | null {
  const text = body?.replace(/\*\*/g, "").trim();
  return text || null;
}

function isoDate(iso: string | undefined): Date | undefined {
  const d = iso ? new Date(iso) : undefined;
  return d && !Number.isNaN(d.getTime()) ? d : undefined;
}

// Excel cells have no timezone; write-excel-file reads a Date's UTC fields.
// Re-stamp the local wall-clock values as UTC so a calendar date stays that
// date (no hidden time part, no off-by-one day east of Greenwich).
export function excelDate(d: Date | undefined): Date | null {
  if (!d) return null;
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds()));
}

function teamName(teamId: string): string | null {
  return TEAMS.find(t => t.id === teamId)?.name ?? (teamId || null);
}

function podName(teamId: string, podId: string): string | null {
  if (!podId) return null;
  const pods = TEAMS.find(t => t.id === teamId)?.pods ?? TEAMS.flatMap(t => t.pods);
  return pods.find(p => p.id === podId)?.name ?? podId;
}

function tagName(id: string): string {
  const t = ALL_TAGS.find(x => x.id === id);
  if (!t) return id;
  return tagNum(t) ? `${tagNum(t)} · ${t.short}` : t.short;
}

// Same order as the list view: by quarter, then earliest start first.
function quarterRank(i: Initiative): number {
  const idx = QUARTERS.findIndex(q => q.id === normalizeQuarterId(i.targetQ));
  return idx === -1 ? Infinity : idx;
}

export function sortForExport(initiatives: Initiative[]): Initiative[] {
  return [...initiatives].sort((a, b) => (quarterRank(a) - quarterRank(b)) || byStart(a, b));
}

const header = (value: string): HeaderCell => ({ value, fontWeight: "bold", backgroundColor: HEADER_FILL });

export function buildInitiativeRows(initiatives: Initiative[], roster: Person[], updates: Update[] = []): (Cell | HeaderCell)[][] {
  const ctx: Ctx = { people: new Map(roster.map(p => [p.id, p])), latest: latestUpdates(updates) };
  return [
    COLUMNS.map(c => header(c.header)),
    ...sortForExport(initiatives).map(i => COLUMNS.map(c => c.value(i, ctx))),
  ];
}

// Plain-language description of the active filters, one line per chip.
export function describeFilters(filters: Filters, query: string, roster: Person[]): [string, string][] {
  const names = (ids: string[], name: (id: string) => string | null | undefined) =>
    ids.map(id => name(id) ?? id).join(", ");
  const lines: [string, string][] = [];
  if (query.trim()) lines.push(["Search", query.trim()]);
  if (filters.quarter.length) lines.push(["Quarter", names(filters.quarter, quarterLabel)]);
  if (filters.status.length) lines.push(["Status", names(filters.status, s => STATUS_LABELS[s as keyof typeof STATUS_LABELS])]);
  if (filters.dri.length) lines.push(["Owner", names(filters.dri, id => roster.find(p => p.id === id)?.name)]);
  if (filters.pillar.length) lines.push(["Tag", names(filters.pillar, tagName)]);
  if (filters.team.length) lines.push(["Team", names(filters.team, teamName)]);
  if (filters.pod.length) lines.push(["Pod", names(filters.pod, id => TEAMS.flatMap(t => t.pods).find(p => p.id === id)?.name)]);
  if (filters.workstream.length) lines.push(["Workstream", names(filters.workstream, id => id === NO_WORKSTREAM ? "No workstream" : id)]);
  return lines;
}

export function hasActiveFilters(filters: Filters, query: string): boolean {
  return !!query.trim() || Object.values(filters).some(v => v.length > 0);
}

export function buildAboutRows(opts: {
  scope: ExportScope; count: number; exportedAt: Date; filters: Filters; query: string; roster: Person[];
  /** "Sep 2026 – Aug 2027" — required for the "window" scope. */
  windowLabel?: string;
}): (Cell | HeaderCell)[][] {
  const rows: (Cell | HeaderCell)[][] = [
    [header("Exported"), excelDate(opts.exportedAt)],
    [header("Scope"), opts.scope === "all" ? "Entire roadmap"
      : opts.scope === "window" ? `Timeline: ${requireWindowLabel(opts.windowLabel)}`
      : "Filtered view"],
    [header("Initiatives"), opts.count],
  ];
  if (opts.scope !== "all") {
    for (const [label, value] of describeFilters(opts.filters, opts.query, opts.roster)) rows.push([header(label), value]);
  }
  rows.push([header("Note"), "Archived initiatives are not included."]);
  return rows;
}

// A timeline export must say which window it covers; a missing label is a
// caller bug, so fail loudly rather than write a vague About sheet.
function requireWindowLabel(label: string | undefined): string {
  if (!label) throw new Error("A timeline export needs its window label");
  return label;
}

export function exportFileName(scope: ExportScope, at: Date): string {
  const suffix = scope === "filtered" ? "-filtered" : scope === "window" ? "-timeline" : "";
  return `digital-roadmap${suffix}-${localISO(at)}.xlsx`;
}

export async function exportRoadmap(opts: {
  scope: ExportScope; initiatives: Initiative[]; roster: Person[]; updates: Update[]; filters: Filters; query: string;
  windowLabel?: string;
}): Promise<void> {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  const exportedAt = new Date();
  await writeXlsxFile([
    {
      sheet: "Roadmap",
      data: buildInitiativeRows(opts.initiatives, opts.roster, opts.updates),
      columns: COLUMNS.map(c => ({ width: c.width })),
      stickyRowsCount: 1,
      dateFormat: "yyyy-mm-dd",
    },
    {
      sheet: "About",
      data: buildAboutRows({ ...opts, count: opts.initiatives.length, exportedAt }),
      columns: [{ width: 14 }, { width: 60 }],
      dateFormat: "yyyy-mm-dd hh:mm",
    },
  ]).toFile(exportFileName(opts.scope, exportedAt));
}
